import {
  deliverySummary,
  deliveryTitle,
  fmtNum,
  lei,
  roDate,
  materialsTsv,
  type LedgerOrder,
  type LedgerShare,
  type SiteLedger,
  t,
  intlLocale,
} from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { DeliveryFiles } from './Deliveries';
import { Icon } from './Icon';
import { SpendBars } from './SpendBars';

/**
 * Ce s-a comandat pe un șantier. Toate cifrele vin din `siteLedger` din
 * @procumi/shared, acoperit de teste; aici doar se așază.
 */

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Un rând al registrului: la comanda împărțită, aceeași cerere are câte un rând pe furnizor. */
const rowKey = (o: { rfqId: string; supplier: { id: string } }) => `${o.rfqId}:${o.supplier.id}`;

/** „2 oct.”: data pe un singur rând, în coloana îngustă a comenzilor. */
const scurt = (iso: string) =>
  new Date(iso).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short' });

/**
 * Cartușul: căsuța cu date din colțul unei planșe, cu rubrici despărțite de
 * linii subțiri. Patru rubrici, fiecare cu explicația ei dedesubt, ca cifra să
 * nu ceară ghicit ce cuprinde.
 */
export function LedgerSummary({ ledger }: { ledger: SiteLedger }) {
  const furnizori = new Set(ledger.orders.map((o) => o.supplier.id)).size;
  const faraOferta = ledger.openCount - ledger.inProgressCount;

  return (
    <section className="cartus sheet" aria-label={t('Pe scurt')}>
      <div className="cartus-cell">
        <span className="cartus-k">{t('Comandat')}</span>
        <b className="cartus-v">{lei(ledger.ordered)}</b>
        <span className="cartus-s">
          {t('fără TVA, din comenzile emise')}
          {ledger.plannedCount ? (
            <>
              {t(
                '· planificat {0} ({1}{2}{3})',
                lei(ledger.planned),
                plural(ledger.plannedCount, t('alegere fără comandă'), t('alegeri fără comandă')),
                ledger.estimatedCount
                  ? `, ${plural(ledger.estimatedCount, t('estimată'), t('estimate'))}`
                  : '',
                ledger.unknownCount ? t(', {0} fără valoare', ledger.unknownCount) : '',
              )}
            </>
          ) : null}
        </span>
      </div>
      <div className="cartus-cell">
        <span className="cartus-k">{t('Comenzi')}</span>
        <b className="cartus-v">{ledger.orders.length}</b>
        <span className="cartus-s">
          {ledger.orders.length
            ? t('de la {0}', plural(furnizori, t('furnizor'), t('furnizori')))
            : t('încă niciuna')}
        </span>
      </div>
      <div className="cartus-cell">
        <span className="cartus-k">{t('În lucru')}</span>
        <b className="cartus-v">{ledger.inProgressCount ? lei(ledger.inProgress) : '—'}</b>
        <span className="cartus-s">
          {ledger.openCount
            ? `${plural(ledger.openCount, t('cerere deschisă'), t('cereri deschise'))}${
                faraOferta ? t(', {0} fără ofertă completă', faraOferta) : ''
              }`
            : t('nicio cerere deschisă')}
        </span>
      </div>
      <div className="cartus-cell">
        <span className="cartus-k">{t('Diferență obținută')}</span>
        <b className={`cartus-v${ledger.gained > 0 ? ' gain' : ''}`}>
          {ledger.gainedCount ? lei(ledger.gained) : '—'}
        </b>
        <span className="cartus-s">
          {ledger.gainedCount
            ? t('sub următoarea ofertă, la {0}', plural(ledger.gainedCount, t('comandă'), t('comenzi')))
            : t('apare când alegi cea mai ieftină din cel puțin două')}
        </span>
      </div>
    </section>
  );
}

const chevron = (
  <Icon>
    <path d="m6 3.5 4.5 4.5L6 12.5" />
  </Icon>
);

/** Comenzile, cele mai noi întâi; fiecare se deschide pe poziții. */
export function LedgerOrders({
  orders,
  onOpenRfq,
  onOpenOrder,
}: {
  orders: LedgerOrder[];
  onOpenRfq: (rfqId: string) => void;
  onOpenOrder: (rfqId: string, orderId: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="sheet">
      <div className="pad" style={{ paddingBottom: 8 }}>
        <div className="section-title">
          <h2>{t('Comenzi')}</h2>
          <span className="muted" style={{ fontSize: 14 }}>
            {t('ce s-a comandat, de la cine')}
          </span>
        </div>
      </div>
      {orders.length ? (
        <div className="orders">
          {orders.map((o) => (
            <OrderRow
              key={rowKey(o)}
              order={o}
              open={open === rowKey(o)}
              onToggle={() => setOpen(open === rowKey(o) ? null : rowKey(o))}
              onOpenRfq={() => onOpenRfq(o.rfqId)}
              onOpenOrder={() => o.order && onOpenOrder(o.rfqId, o.order.id)}
            />
          ))}
        </div>
      ) : (
        <div className="empty">
          {t(
            'Nicio comandă încă. Când alegi furnizorul la o cerere („Cu cine ai rămas?”), comanda apare aici, cu pozițiile și banii ei.',
          )}
        </div>
      )}
    </section>
  );
}

function OrderRow({
  order: o,
  open,
  onToggle,
  onOpenRfq,
  onOpenOrder,
}: {
  order: LedgerOrder;
  open: boolean;
  onToggle: () => void;
  onOpenRfq: () => void;
  onOpenOrder: () => void;
}) {
  const id = `comanda-${rowKey(o)}`;
  // Valoarea trecută de om poate diferi de oferta după care s-a ales: s-a negociat.
  const negociat =
    !o.order &&
    !o.estimated &&
    o.value != null &&
    o.offerTotal != null &&
    Math.abs(o.value - o.offerTotal) >= 1;

  return (
    <div className={`order${open ? ' open' : ''}`}>
      <button className="order-row" aria-expanded={open} aria-controls={id} onClick={onToggle}>
        <span className="order-date">{scurt(o.at)}</span>
        <span className="order-main">
          <strong>{o.supplier.name}</strong>
          <span className="sub">
            <span className="pill cat">{o.category}</span>
            {o.order ? <span className="order-no">{o.order.number}</span> : null} {o.title}
            {o.note ? <span className="order-note"> · „{o.note}”</span> : null}
            {o.order?.deliveries.length ? (
              <span className="order-dlv"> · {deliverySummary(o.order.deliveries)}</span>
            ) : null}
          </span>
        </span>
        <span className="order-val">
          <b>{o.value == null ? '—' : lei(o.value)}</b>
          {o.value == null ? (
            <span className="muted">{t('fără valoare')}</span>
          ) : o.estimated ? (
            <span className="muted">{t('estimată, după ofertă')}</span>
          ) : o.gap != null && o.gap >= 1 ? (
            <span className="gain">{t('cu {0} sub următoarea', lei(o.gap))}</span>
          ) : o.gap != null && o.gap <= -1 ? (
            <span className="over">{t('cu {0} peste cea mai ieftină', lei(-o.gap))}</span>
          ) : null}
        </span>
        <span className="order-chev">{chevron}</span>
      </button>
      {open ? (
        <div className="order-lines" id={id}>
          <table className="ledger-table">
            <thead>
              <tr>
                <th>{t('Poziție')}</th>
                <th className="n">{t('Cantitate')}</th>
                <th className="n">{t('Preț unitar')}</th>
                <th className="n">{t('Valoare')}</th>
              </tr>
            </thead>
            <tbody>
              {o.lines.map((l) => (
                <tr key={l.itemId}>
                  <td>{l.desc}</td>
                  <td className="n">
                    {fmtNum(l.qty)} {l.unit}
                  </td>
                  <td className="n">{l.unitPrice == null ? '—' : fmtNum(l.unitPrice)}</td>
                  <td className="n">{l.value == null ? t('necotată') : lei(l.value)}</td>
                </tr>
              ))}
            </tbody>
            {o.offerTotal != null ? (
              <tfoot>
                <tr>
                  <td colSpan={3}>{t('După ofertă, cu transportul și costurile pe comandă')}</td>
                  <td className="n">{lei(o.offerTotal)}</td>
                </tr>
                {negociat ? (
                  <tr>
                    <td colSpan={3}>{t('Valoarea comenzii, trecută de tine')}</td>
                    <td className="n strong">{lei(o.value!)}</td>
                  </tr>
                ) : null}
              </tfoot>
            ) : null}
          </table>
          {o.order && o.order.deliveries.length ? (
            <ul className="dlv-list in-ledger" aria-label={t('Livrări și avize')}>
              {o.order.deliveries.map((d) => (
                <li key={d.id} className={d.kind === 'return' ? 'ret' : undefined}>
                  <span className="dlv-date">{roDate(d.date)}</span>
                  <div className="dlv-main">
                    <b>{deliveryTitle(d)}</b>
                    {d.note ? <span className="dlv-note">{d.note}</span> : null}
                    <DeliveryFiles
                      delivery={d}
                      fileUrl={(f) => api.deliveryFileUrl(o.rfqId, o.order!.id, d.id, f)}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="order-foot">
            <span className="muted">
              {o.order
                ? t('Pozițiile din comanda {0}, cum a fost emisă; valoarea e fără TVA.', o.order.number)
                : t(
                    'Cantitățile sunt cele cerute, cu prețurile ofertei alese. Fă comanda din cerere ca să rămână ce s-a negociat.',
                  )}
            </span>
            <span className="order-links">
              {o.order ? (
                <button className="link" onClick={onOpenOrder}>
                  {t('Deschide comanda')}
                </button>
              ) : null}
              <button className="link" onClick={onOpenRfq}>
                {t('Vezi cererea și comparația')}
              </button>
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Banii pe categorii și pe furnizori: două foi alăturate, aceleași bare ca pe „Azi”. */
export function LedgerShares({
  byCategory,
  bySupplier,
}: {
  byCategory: LedgerShare[];
  bySupplier: LedgerShare[];
}) {
  const rows = (s: LedgerShare[]) => s.map((x) => ({ siteId: null, label: x.label, total: x.total }));
  return (
    <div className="panels even">
      <section className="sheet panel">
        <div className="pad">
          <div className="section-title">
            <h2>{t('Pe categorii')}</h2>
            <span className="muted" style={{ fontSize: 14 }}>
              {t('lei fără TVA')}
            </span>
          </div>
        </div>
        <SpendBars rows={rows(byCategory)} />
      </section>
      <section className="sheet panel">
        <div className="pad">
          <div className="section-title">
            <h2>{t('Pe furnizori')}</h2>
            <span className="muted" style={{ fontSize: 14 }}>
              {t('lei fără TVA')}
            </span>
          </div>
        </div>
        <SpendBars rows={rows(bySupplier)} />
      </section>
    </div>
  );
}

/** Câte materiale se văd înainte de „Arată toate”. */
const MATERIALE = 10;

/** Ce s-a cumpărat pe tot șantierul, adunat pe material, cu prețul mediu. */
export function LedgerMaterials({ ledger, toast }: { ledger: SiteLedger; toast: Toast }) {
  const [all, setAll] = useState(false);
  const shown = all ? ledger.materials : ledger.materials.slice(0, MATERIALE);

  return (
    <section className="sheet">
      <div className="pad" style={{ paddingBottom: 12 }}>
        <div className="section-title" style={{ marginBottom: 0 }}>
          <h2>{t('Materiale')}</h2>
          <button className="btn small" onClick={() => void copyText(materialsTsv(ledger.materials), toast)}>
            {t('Copiază pentru Excel')}
          </button>
        </div>
      </div>
      <div className="ledger-wrap">
        <table className="ledger-table materials">
          <thead>
            <tr>
              <th>{t('Material')}</th>
              <th className="n">{t('Cantitate')}</th>
              <th className="n">{t('Preț mediu')}</th>
              <th className="n">{t('Valoare')}</th>
              <th className="n">{t('Comenzi')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m) => (
              <tr key={`${m.desc}|${m.unit}`}>
                <td>{m.desc}</td>
                <td className="n">
                  {fmtNum(Math.round(m.qty * 100) / 100)} {m.unit}
                </td>
                <td className="n">
                  {m.avgPrice == null ? '—' : t('{0} lei', fmtNum(Math.round(m.avgPrice * 100) / 100))}
                </td>
                <td className="n strong">{m.value ? lei(m.value) : '—'}</td>
                <td className="n">{m.orders}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ledger-note">
        <span className="muted">
          {t('Adunate pe denumire și unitate de măsură. Prețul mediu e ponderat cu cantitățile.')}
        </span>
        {ledger.materials.length > MATERIALE ? (
          <button className="link" onClick={() => setAll(!all)}>
            {all ? t('Arată mai puține') : t('Arată toate ({0})', ledger.materials.length)}
          </button>
        ) : null}
      </div>
    </section>
  );
}
